// @flow
import React from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { styled } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { useGlobalStyles } from 'GlobalStyles';
import usePageTitle from 'hooks/usePageTitle';
import strings from 'strings';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import { FPCard, FPCardContent } from 'UI/components/atoms/FPCard';
import SaveButton from 'UI/components/atoms/SaveButton';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FileManager from 'UI/components/organisms/FileManager';
import ContentPageLayout from 'UI/components/templates/ContentPageLayout';
import { AppName } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';

import { useNewCandidateFromResume } from './NewCandidateFromResume.hooks';

const PARSER_CATEGORIES = [
  {
    id: 44,
    title: 'Resume'
  }
];

export const ParsingAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const getAllowedExtensions = () => {
  const envValue =
    window.GPAC_ENV?.ALLOWED_EXTENSIONS_FOR_RESUME_PARSER ||
    process.env.REACT_APP_ALLOWED_EXTENSIONS_FOR_RESUME_PARSER;

  const defaultExtensions = ['pdf', 'doc', 'docx'];

  if (!envValue || typeof envValue !== 'string') {
    return defaultExtensions;
  }

  const extensions = envValue
    .split(',')
    .map(ext => ext.trim())
    .filter(ext => ext.length > 0);

  return extensions.length > 0 ? extensions : defaultExtensions;
};

const ALLOWED_EXTENSIONS = getAllowedExtensions();

export const NewCandidatesFromResume = () => {
  const globalClasses = useGlobalStyles();
  const {
    canStart,
    handleClickSecondaryButton,
    handleFilesChanged,
    handleReset,
    handleSubmit,
    hideSuccessAlert,
    resetFlow,
    uiState
  } = useNewCandidateFromResume();

  usePageTitle({
    title: `${AppName} | ${strings.candidates.creation.fromResume.title}`
  });

  return (
    <ContentPageLayout
      text={strings.candidates.creation.fromResume.title}
      titleLabelProps={{ backNavigation: true }}
    >
      <div className={globalClasses.itemCreationDataWrapper}>
        <FPCard className={globalClasses.newItemsSection}>
          <FPCardContent
            variant="relaxed"
            headerProps={{
              title: strings.candidates.creation.fromResume.cardTitle,
              subheader: strings.candidates.creation.fromResume.cardSubtitle,
              variant: 'section'
            }}
          >
            {uiState.showSuccessAlert && (
              <ParsingAlert
                action={
                  <Button color="inherit" size="small" onClick={hideSuccessAlert}>
                    {strings.shared.ui.close}
                  </Button>
                }
                severity="success"
                variant="filled"
              >
                {strings.candidates.creation.fromResume.successAlert}
              </ParsingAlert>
            )}

            <FileManager
              allowedExtensions={ALLOWED_EXTENSIONS}
              allowMoveFiles={false}
              apiVersionForDeleting={1}
              defaultCategories={PARSER_CATEGORIES}
              layoutInColumns
              filesEndpoint={Endpoints.Files}
              module="resume-parser"
              onAttachmentsChanged={handleFilesChanged}
              onReset={handleReset}
              shouldReset={uiState.shouldReset}
              showFileSize
              showItemMenu
              viewItemOnClick
            />
          </FPCardContent>
        </FPCard>
        <Grid container spacing={2} justify="flex-end">
          <Grid item>
            <FPActionButton
              text={strings.candidates.creation.fromResume.secondaryButton}
              onClick={handleClickSecondaryButton}
              variant="outlined"
            />
          </Grid>
          <Grid item>
            <SaveButton
              initialText={strings.candidates.creation.fromResume.cta}
              onProgressText={strings.shared.ui.working}
              type="button"
              isSaving={uiState.isSaving}
              isSuccess={uiState.isSuccess}
              disabled={!canStart || uiState.isSaving}
              onClick={handleSubmit}
            />
          </Grid>
        </Grid>
        {uiState.isSuccess && (
          <DecisionDialog
            confirmButtonText={strings.shared.ui.gotIt}
            isHighLightActionOnLeft
            message={strings.candidates.creation.fromResume.successBody}
            onClose={resetFlow}
            onConfirm={resetFlow}
            severity="success"
            showSeverityIcon
            title={strings.candidates.creation.fromResume.successTitle}
            withButtons="Confirmation"
          />
        )}
      </div>
    </ContentPageLayout>
  );
};
